import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  getDocs, 
  getDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from './config';
import { logActivity, ACTIONS, ENTITY_TYPES } from './activityLogService';

const SUPPLIERS_COLLECTION = 'suppliers';

/**
 * Create a new supplier
 */
export const createSupplier = async (supplierData, currentUser) => {
  try {
    const supplierRef = await addDoc(collection(db, SUPPLIERS_COLLECTION), {
      ...supplierData,
      totalPurchased: 0,
      totalPaid: 0,
      currentBalance: 0,
      status: 'active',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      createdBy: currentUser?.uid || 'system'
    });

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: ACTIONS.CREATED_SUPPLIER,
      entityType: ENTITY_TYPES.SUPPLIER,
      entityId: supplierRef.id,
      entityName: supplierData.name,
      details: { name: supplierData.name, phone: supplierData.phone || '', email: supplierData.email || '' }
    });

    return { success: true, id: supplierRef.id };
  } catch (error) {
    console.error('Error creating supplier:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all suppliers
 */
export const getSuppliers = async (filters = {}) => {
  try {
    // Fetch all then filter in memory to avoid composite index requirement
    const snapshot = await getDocs(query(collection(db, SUPPLIERS_COLLECTION), orderBy('name', 'asc')));
    let suppliers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    if (filters.status) {
      suppliers = suppliers.filter(s => s.status === filters.status);
    }

    return { success: true, data: suppliers };
  } catch (error) {
    console.error('Error getting suppliers:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get supplier by ID
 */
export const getSupplierById = async (supplierId) => {
  try {
    const supplierDoc = await getDoc(doc(db, SUPPLIERS_COLLECTION, supplierId));
    
    if (!supplierDoc.exists()) {
      return { success: false, error: 'Supplier not found' };
    }

    return { 
      success: true, 
      data: { id: supplierDoc.id, ...supplierDoc.data() } 
    };
  } catch (error) {
    console.error('Error getting supplier:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update supplier
 */
export const updateSupplier = async (supplierId, updates, currentUser) => {
  try {
    const supplierRef = doc(db, SUPPLIERS_COLLECTION, supplierId);
    
    await updateDoc(supplierRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: ACTIONS.UPDATED_SUPPLIER,
      entityType: ENTITY_TYPES.SUPPLIER,
      entityId: supplierId,
      entityName: updates.name || supplierId,
      details: { name: updates.name || supplierId }
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating supplier:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update supplier financial summary
 */
export const updateSupplierFinancials = async (supplierId, financialData) => {
  try {
    const supplierRef = doc(db, SUPPLIERS_COLLECTION, supplierId);
    
    await updateDoc(supplierRef, {
      totalPurchased: financialData.totalPurchased || 0,
      totalPaid: financialData.totalPaid || 0,
      currentBalance: financialData.currentBalance || 0,
      updatedAt: Timestamp.now()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating supplier financials:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete supplier (soft delete - set status to inactive)
 */
export const deleteSupplier = async (supplierId, currentUser) => {
  try {
    const supplierRef = doc(db, SUPPLIERS_COLLECTION, supplierId);
    
    // Check if supplier has outstanding balance
    const supplierDoc = await getDoc(supplierRef);
    if (supplierDoc.exists() && supplierDoc.data().currentBalance > 0) {
      return { 
        success: false, 
        error: 'Cannot delete supplier with outstanding balance' 
      };
    }

    await updateDoc(supplierRef, {
      status: 'inactive',
      updatedAt: Timestamp.now()
    });

    await logActivity({
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      userName: currentUser?.displayName || currentUser?.email,
      action: ACTIONS.DELETED_SUPPLIER,
      entityType: ENTITY_TYPES.SUPPLIER,
      entityId: supplierId,
      entityName: supplierDoc.data().name,
      details: { name: supplierDoc.data().name }
    });

    return { success: true };
  } catch (error) {
    console.error('Error deleting supplier:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get supplier statistics
 */
export const getSupplierStats = async (supplierId) => {
  try {
    // Get supplier data
    const supplierResult = await getSupplierById(supplierId);
    if (!supplierResult.success) {
      return supplierResult;
    }

    // Get purchase orders count
    const posQuery = query(
      collection(db, 'purchaseOrders'),
      where('supplierId', '==', supplierId)
    );
    const posSnapshot = await getDocs(posQuery);

    // Get payables count
    const payablesQuery = query(
      collection(db, 'tradePayables'),
      where('supplierId', '==', supplierId)
    );
    const payablesSnapshot = await getDocs(payablesQuery);

    const stats = {
      ...supplierResult.data,
      totalPurchaseOrders: posSnapshot.size,
      totalPayables: payablesSnapshot.size,
      outstandingPayables: payablesSnapshot.docs.filter(
        doc => doc.data().status !== 'paid'
      ).length
    };

    return { success: true, data: stats };
  } catch (error) {
    console.error('Error getting supplier stats:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Search suppliers by name
 */
export const searchSuppliers = async (searchTerm) => {
  try {
    const suppliersSnapshot = await getDocs(collection(db, SUPPLIERS_COLLECTION));
    
    const suppliers = suppliersSnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(supplier => 
        supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        supplier.status === 'active'
      );

    return { success: true, data: suppliers };
  } catch (error) {
    console.error('Error searching suppliers:', error);
    return { success: false, error: error.message, data: [] };
  }
};
